import type { RawSpecSide, RawVersionMetadata } from "../types";
import { ElementModel } from "./ElementModel";
import { GeneratorSideModel } from "./GeneratorSideModel";
import { ObservableModelNode } from "./observable";
import { PartModel } from "./PartModel";
import type { AddLayerOptions } from "./StyleModel";
import {
  cloneValue,
  expectObject,
  readObjectArray,
  readString,
  splitKnown,
} from "./utils";
import type { JsonObject } from "./utils";
import {
  VersionPositions,
  type VersionPositionStyleAssignment,
} from "./versionModel/VersionPositions";
import type { SpecsModelPositionSnapshot } from "./types";
import type { VersionLayerPath, VersionSideName } from "./versionModel/types";

type RawPositionForPixelToInch = {
  generator: {
    allowIndependentScaling?: boolean;
    boxWidthInches?: number;
    sides: Array<{ w: number }>;
  };
};

function computeDefaultPixelToInch(
  rawPositions: RawPositionForPixelToInch[]
): number {
  for (const position of rawPositions) {
    const { generator } = position;
    if (generator?.allowIndependentScaling === true) {
      continue;
    }

    const boxWidth = generator?.boxWidthInches;

    if (!boxWidth) {
      return 1;
    }

    return Math.round(generator.sides[0].w / boxWidth);
  }

  return 1;
}

export class VersionModel extends ObservableModelNode {
  name!: string;
  pixelToInch!: number;
  parts!: PartModel[];
  positions!: SpecsModelPositionSnapshot[];
  positionGroups!: unknown[];
  extra!: JsonObject;
  generatorExtra!: JsonObject;
  generatorSides!: GeneratorSideModel[];

  constructor() {
    super();
    this.defineObservableValue("name", "");
    this.defineObservableValue("pixelToInch", 1);
    this.defineObservableValue("parts", []);
    this.defineObservableValue("positions", []);
    this.defineObservableValue("positionGroups", []);
    this.defineObservableValue("extra", {});
    this.defineObservableValue("generatorExtra", {});
    this.defineObservableValue("generatorSides", []);
  }

  restore(data: unknown) {
    const source = expectObject(data, "version");
    const generator = source.generator
      ? expectObject(source.generator, "version.generator")
      : {};

    this.extra = splitKnown(source, [
      "name",
      "pixelToInch",
      "parts",
      "positions",
      "positionGroups",
      "generator",
    ]);
    this.generatorExtra = splitKnown(generator, ["sides"]);
    this.name = readString(source, "name");
    if (typeof source.pixelToInch === "number") {
      this.pixelToInch = source.pixelToInch;
    } else if (source.positions) {
      this.pixelToInch = computeDefaultPixelToInch(
        source.positions as RawPositionForPixelToInch[]
      );
    } else {
      this.pixelToInch = 1;
    }

    this.parts = readObjectArray(source, "parts").map((part) =>
      new PartModel().restore(part)
    );
    this.positions = cloneValue(
      Array.isArray(source.positions) ? source.positions : []
    ) as SpecsModelPositionSnapshot[];
    this.positionGroups = cloneValue(
      Array.isArray(source.positionGroups) ? source.positionGroups : []
    );
    this.generatorSides = readObjectArray(generator, "sides").map((side) =>
      new GeneratorSideModel().restore(side)
    );

    return this;
  }

  getPart(name: string) {
    return this.parts.find((part) => part.name === name);
  }

  getGlobalSide(sideName: VersionSideName) {
    return this.findGlobalSide(sideName);
  }

  getElement(elementName: string, partName?: string) {
    if (partName) {
      const part = this.getPart(partName);
      const element = part?.elements.find((item) => item.name === elementName);
      return part && element ? { part, element } : null;
    }

    for (const part of this.parts) {
      const element = part.elements.find((item) => item.name === elementName);
      if (element) {
        return { part, element };
      }
    }

    return null;
  }

  addPart(name: string) {
    const partName = name.trim();
    if (!partName || this.getPart(partName)) {
      return false;
    }

    this.parts.push(
      new PartModel().restore({
        name: partName,
        mainElement: "",
        elements: [],
      })
    );
    return true;
  }

  renamePart(name: string, nextName: string) {
    const part = this.getPart(name);
    const trimmedName = nextName.trim();
    if (!part || !trimmedName || trimmedName === name) {
      return false;
    }
    if (this.getPart(trimmedName)) {
      return false;
    }

    part.name = trimmedName;
    this.parts.forEach((candidate) => {
      if (candidate.inheritFrom === name) {
        candidate.setInheritFrom(trimmedName);
      }
    });
    this.versionPositions.renamePartInPositionGroupRules(name, trimmedName);
    return true;
  }

  removePart(name: string) {
    if (this.parts.length <= 1) {
      return false;
    }

    const index = this.parts.findIndex((part) => part.name === name);
    if (index === -1) {
      return false;
    }

    this.parts.splice(index, 1);
    this.parts.forEach((part) => {
      if (part.inheritFrom === name) {
        part.setInheritFrom(null);
      }
    });
    this.versionPositions.removePartFromPositionGroupRules(name);
    return true;
  }

  reorderPart(name: string, targetIndex: number) {
    const currentIndex = this.parts.findIndex((part) => part.name === name);
    if (
      currentIndex === -1 ||
      targetIndex < 0 ||
      targetIndex >= this.parts.length
    ) {
      return false;
    }

    const [part] = this.parts.splice(currentIndex, 1);
    this.parts.splice(targetIndex, 0, part);
    return true;
  }

  addElement(name: string, partName = "body") {
    const targetPart = this.getPart(partName);
    if (!targetPart || targetPart.elements.some((item) => item.name === name)) {
      return false;
    }

    const defaultStyleName = "solid";
    targetPart.elements.push(
      new ElementModel().restore({
        name,
        defaultStyle: defaultStyleName,
        styles: [
          {
            name: defaultStyleName,
            defaultLayer: "",
            display: {
              displayable: true,
              menuIconUrl: "",
              displayMethod: "icon",
            },
          },
        ],
        display: {
          displayable: true,
          menuIconUrl: "",
          displayMethod: "icon",
        },
      })
    );
    if (!targetPart.mainElement) {
      targetPart.mainElement = name;
    }
    return true;
  }

  addLayer(args: VersionLayerPath, options: AddLayerOptions = {}) {
    const part = this.getPart(args.partName);
    const element = part?.getElement(args.elementName);
    const style = element?.getStyle(args.styleName);
    if (!part || !element || !style || !args.layerName) {
      return null;
    }

    const layer = style.addLayer(args.layerName, options);
    return layer ? { part, element, style, layer } : null;
  }

  moveElementToPart(
    fromPartName: string,
    elementName: string,
    toPartName: string
  ) {
    if (fromPartName === toPartName) {
      return false;
    }

    const fromPart = this.getPart(fromPartName);
    const toPart = this.getPart(toPartName);
    if (!fromPart || !toPart) {
      return false;
    }
    if (toPart.elements.some((element) => element.name === elementName)) {
      return false;
    }

    const elementIndex = fromPart.elements.findIndex(
      (element) => element.name === elementName
    );
    if (elementIndex === -1) {
      return false;
    }

    const [element] = fromPart.elements.splice(elementIndex, 1);
    toPart.elements.push(element);
    if (fromPart.mainElement === elementName) {
      fromPart.mainElement = fromPart.elements[0]?.name || "";
    }
    if (!toPart.mainElement) {
      toPart.mainElement = elementName;
    }
    this.versionPositions.moveElementInPositionGroupRules(
      fromPartName,
      elementName,
      toPartName
    );
    return true;
  }

  removeElement(name: string, partName?: string) {
    let removed = false;
    const targetParts = partName
      ? this.parts.filter((part) => part.name === partName)
      : this.parts;

    targetParts.forEach((part) => {
      const nextElements = part.elements.filter((item) => item.name !== name);
      if (nextElements.length === part.elements.length) {
        return;
      }

      removed = true;
      part.elements.splice(0, part.elements.length, ...nextElements);
      if (part.mainElement === name) {
        part.mainElement = nextElements[0]?.name || "";
      }
    });

    return removed;
  }

  updateElement(
    name: string,
    elementOptions: Partial<{
      name: string;
      inherit: boolean;
      inheritFromGroup: string;
      displayable: boolean;
      menuIconUrl: string;
    }>,
    partName?: string
  ) {
    const target = this.getElement(name, partName);
    if (!target) {
      return false;
    }
    if (
      elementOptions.inheritFromGroup !== undefined &&
      !target.part.hasInheritanceGroup(elementOptions.inheritFromGroup)
    ) {
      return false;
    }

    const previousName = target.element.name;
    if (elementOptions.name !== undefined) {
      target.element.name = elementOptions.name;
      if (target.part.mainElement === previousName) {
        target.part.mainElement = elementOptions.name;
      }
    }
    if (elementOptions.inherit !== undefined) {
      target.element.setInheritance({ inherit: elementOptions.inherit });
    }
    if (elementOptions.inheritFromGroup !== undefined) {
      target.element.setInheritance({
        inheritFromGroup: elementOptions.inheritFromGroup,
      });
    }
    if (elementOptions.displayable !== undefined) {
      target.element.displayable = elementOptions.displayable;
    }
    if (elementOptions.menuIconUrl !== undefined) {
      target.element.menuIconUrl = elementOptions.menuIconUrl;
    }
    return true;
  }

  updatePart(
    name: string,
    partOptions: Partial<{
      mainElement: string;
      required: boolean;
      inheritFrom: string | null;
    }>
  ) {
    const part = this.getPart(name);
    if (!part) {
      return false;
    }

    if (
      partOptions.mainElement !== undefined &&
      !part.setMainElement(partOptions.mainElement)
    ) {
      return false;
    }
    if (partOptions.required !== undefined) {
      part.setRequired(partOptions.required);
    }
    if (partOptions.inheritFrom !== undefined) {
      part.setInheritFrom(partOptions.inheritFrom);
    }
    return true;
  }

  setActiveStyle(partName: string, elementName: string, styleName: string) {
    const element = this.getPart(partName)?.elements.find(
      (item) => item.name === elementName
    );
    if (!element) {
      return false;
    }

    element.defaultStyle = styleName;
    return true;
  }

  getPositions() {
    return this.versionPositions.getPositions();
  }

  getPositionGroups() {
    return this.versionPositions.getPositionGroups();
  }

  getPositionDecorationTypeNames() {
    return this.versionPositions.getDecorationTypeNames();
  }

  getPositionStyleAssignments(): VersionPositionStyleAssignment[] {
    return this.versionPositions.getPositionStyleAssignments();
  }

  addPosition(position: any) {
    return this.versionPositions.addPosition(position);
  }

  updatePosition(previousId: string, position: SpecsModelPositionSnapshot) {
    return this.versionPositions.updatePosition(previousId, position);
  }

  removePosition(positionId: string) {
    return this.versionPositions.removePosition(positionId);
  }

  replacePositionIdInStyleAssignments(previousId: string, nextId: string) {
    return this.versionPositions.replacePositionIdInStyleAssignments(
      previousId,
      nextId
    );
  }

  removePositionIdFromStyleAssignments(positionId: string) {
    return this.versionPositions.removePositionIdFromStyleAssignments(
      positionId
    );
  }

  setPositionData(args: {
    positions: SpecsModelPositionSnapshot[];
    positionGroups?: any[] | null;
    stylePositionIds?: Record<string, string[]> | null;
  }) {
    return this.versionPositions.setPositionData(args);
  }

  addPositionGroup(group: any) {
    return this.versionPositions.addPositionGroup(group);
  }

  updatePositionGroup(previousName: string, group: any) {
    return this.versionPositions.updatePositionGroup(previousName, group);
  }

  removePositionGroup(groupName: string) {
    return this.versionPositions.removePositionGroup(groupName);
  }

  setStylePositionIds(styleKey: string, positionIds: string[]) {
    return this.versionPositions.setStylePositionIds(styleKey, positionIds);
  }

  getSelectedPosition(selectedPositionId: string | null) {
    return this.versionPositions.getSelectedPosition(selectedPositionId);
  }

  mutateSelectedPosition(
    selectedPositionId: string | null,
    mutator: (position: any) => void
  ) {
    return this.versionPositions.mutateSelectedPosition(
      selectedPositionId,
      mutator
    );
  }

  getSelectedPositionSideSnapshots(selectedPositionId: string | null) {
    return this.versionPositions.getSelectedPositionSideSnapshots(
      selectedPositionId
    );
  }

  save() {
    return {
      ...cloneValue(this.extra),
      name: this.name,
      pixelToInch: this.pixelToInch,
      parts: this.parts.map((part) => part.save()),
      positions: cloneValue(this.positions),
      positionGroups: cloneValue(this.positionGroups),
      generator: {
        ...cloneValue(this.generatorExtra),
        sides: this.generatorSides.map((side) => side.save()),
      },
    };
  }

  getRawVersionMetadata(): RawVersionMetadata | null {
    const metadata = cloneValue(this.extra);

    if (Object.keys(this.generatorExtra).length > 0) {
      metadata.generator = cloneValue(this.generatorExtra);
    }

    return Object.keys(metadata).length > 0 ? metadata : null;
  }

  setRawVersionMetadata(rawVersionMetadata: RawVersionMetadata | null) {
    if (rawVersionMetadata == null) {
      this.extra = {};
      this.generatorExtra = {};
      return true;
    }

    if (
      typeof rawVersionMetadata !== "object" ||
      Array.isArray(rawVersionMetadata)
    ) {
      throw new Error("rawVersionMetadata must be an object");
    }

    const rawMetadata = cloneValue(rawVersionMetadata);

    for (const key of [
      "name",
      "pixelToInch",
      "parts",
      "positions",
      "positionGroups",
    ]) {
      if (key in rawMetadata) {
        throw new Error(`rawVersionMetadata cannot override version.${key}`);
      }
    }

    const rawGeneratorMetadata = rawMetadata.generator;
    delete rawMetadata.generator;

    if (
      rawGeneratorMetadata != null &&
      (typeof rawGeneratorMetadata !== "object" ||
        Array.isArray(rawGeneratorMetadata))
    ) {
      throw new Error("rawVersionMetadata.generator must be an object");
    }

    if (
      rawGeneratorMetadata &&
      Object.prototype.hasOwnProperty.call(rawGeneratorMetadata, "sides")
    ) {
      throw new Error(
        "rawVersionMetadata.generator cannot override version.generator.sides"
      );
    }

    this.extra = rawMetadata;
    this.generatorExtra = cloneValue(rawGeneratorMetadata ?? {});
    return true;
  }

  getLayer(args: VersionLayerPath) {
    const part = this.getPart(args.partName);
    const element = part?.getElement(args.elementName);
    const style = element?.getStyle(args.styleName);
    const layer = style?.getLayer(args.layerName) ?? null;

    return part && element && style && layer
      ? { part, element, style, layer }
      : null;
  }

  getInheritanceGroups(partName?: string) {
    const parts = partName
      ? this.parts.filter((part) => part.name === partName)
      : this.parts;

    return Array.from(
      new Set(parts.flatMap((part) => part.getInheritanceGroups()))
    ).sort();
  }

  hasInheritanceGroup(groupName: string, partName?: string) {
    return (
      !groupName || this.getInheritanceGroups(partName).includes(groupName)
    );
  }

  setGlobalMaskUrl(sideName: VersionSideName, url: string | null) {
    const side = this.ensureGlobalSide(sideName);
    side.imageUrl = url;
    this.cleanupGlobalSide(sideName);
    return true;
  }

  setGlobalHighlightUrl(sideName: VersionSideName, url: string | null) {
    const side = this.ensureGlobalSide(sideName);
    side.highlightUrl = url;
    this.cleanupGlobalSide(sideName);
    return true;
  }

  private findGlobalSide(sideName: VersionSideName) {
    return this.generatorSides.find((side) => side.name === sideName) ?? null;
  }

  private createDefaultGlobalSide(name: VersionSideName): RawSpecSide {
    return {
      name,
      zIndex: 150001,
      zIndexHighlight: 150000,
      imageUrl: "",
      highlightUrl: "",
      alwaysRedraw: false,
    };
  }

  private ensureGlobalSide(sideName: VersionSideName) {
    const existing = this.findGlobalSide(sideName);
    if (existing) {
      return existing;
    }

    const side = new GeneratorSideModel().restore(
      this.createDefaultGlobalSide(sideName)
    );
    this.generatorSides.push(side);
    return side;
  }

  private cleanupGlobalSide(sideName: VersionSideName) {
    const side = this.findGlobalSide(sideName);
    if (!side) {
      return;
    }

    if (side.imageUrl || side.highlightUrl) {
      return;
    }

    this.generatorSides = this.generatorSides.filter(
      (candidate) => candidate !== side
    );
  }

  private get versionPositions() {
    return new VersionPositions(this);
  }
}
